import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  WATERMARK_JOB_PRESETS,
  getWatermarkPresetFeatureLines,
  getWatermarkPresetBadge,
} from "../../../../config/tools/watermarkPresets";
import "./WatermarkPresetMenu.css";

/**
 * Floating watermark job-preset picker (docks beside left panel).
 */
const WatermarkPresetMenu = ({
  isOpen = false,
  onClose = () => {},
  selectedId = null,
  onSelect = () => {},
  anchorSelector = null,
  anchorTopSelector = null,
  anchorGap = 10,
  ignoreCloseSelectors = ["#wmc-preset-trigger"],
}) => {
  const menuRef = useRef(null);
  const [position, setPosition] = useState({ x: 380, y: 200 });

  const placeBesideAnchor = useCallback(() => {
    if (!anchorSelector) return;
    const anchor = document.querySelector(anchorSelector);
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const topEl = anchorTopSelector
      ? document.querySelector(anchorTopSelector)
      : null;
    const topRect = topEl?.getBoundingClientRect?.() || rect;

    const menuWidth = 340;
    const menuHeight = 560;
    let x = rect.right + anchorGap;
    let y = topRect.top;

    const maxX = window.innerWidth - menuWidth - 12;
    const maxY =
      window.innerHeight - Math.min(menuHeight, window.innerHeight - 24);
    if (x > maxX) {
      x = Math.max(12, rect.left - menuWidth - anchorGap);
    }
    y = Math.max(12, Math.min(y, maxY));
    setPosition({ x, y });
  }, [anchorGap, anchorSelector, anchorTopSelector]);

  useEffect(() => {
    if (!isOpen) return undefined;
    placeBesideAnchor();
    const onResize = () => placeBesideAnchor();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isOpen, placeBesideAnchor]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      for (const sel of ignoreCloseSelectors) {
        if (typeof sel === "string" && e.target.closest?.(sel)) return;
      }
      onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ignoreCloseSelectors, isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="wmc-preset-menu"
      ref={menuRef}
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      role="dialog"
      aria-label="Watermark presets"
    >
      <div className="wmc-preset-menu__header">
        <span className="wmc-preset-menu__title">Presets</span>
        <span className="wmc-preset-menu__hint">Job-ready layouts</span>
      </div>
      <div className="wmc-preset-menu__list" role="listbox">
        {WATERMARK_JOB_PRESETS.map((preset) => {
          const selected = selectedId === preset.id;
          return (
            <button
              key={preset.id}
              type="button"
              role="option"
              aria-selected={selected}
              className={`wmc-preset-card${selected ? " is-selected" : ""}`}
              onClick={() => {
                onSelect(preset);
                onClose();
              }}
            >
              <div
                className={`wmc-preset-card__thumb wmc-preset-card__thumb--${preset.preview}`}
                aria-hidden="true"
              >
                <span className="wmc-preset-card__stage" />
                <span className="wmc-preset-card__mark" />
                <span className="wmc-preset-card__mark wmc-preset-card__mark--b" />
                <span className="wmc-preset-card__mark wmc-preset-card__mark--c" />
              </div>
              <div className="wmc-preset-card__body">
                <div className="wmc-preset-card__header">
                  <span className="wmc-preset-card__title">{preset.title}</span>
                  <span className="wmc-preset-card__badge">
                    {getWatermarkPresetBadge(preset)}
                  </span>
                </div>
                <ul className="wmc-preset-card__features">
                  {getWatermarkPresetFeatureLines(preset).map((line, index, lines) => {
                    const isLast = index === lines.length - 1;
                    return (
                      <li
                        key={line}
                        className={isLast ? "wmc-preset-card__features-row" : undefined}
                      >
                        <span>{line}</span>
                        {isLast ? (
                          <span
                            className={`wmc-preset-card__check${
                              selected ? " is-checked" : ""
                            }`}
                            aria-hidden="true"
                          >
                            {selected ? "✓" : null}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default WatermarkPresetMenu;
